import { useApp } from '@/context/AppContext'
import { useFilteredBreakValues } from '@/hooks/useFilteredBreakValues'
import { HorizontalStackedBar } from '@/components/charts/HorizontalStackedBar'
import { buildFilterKey } from '@/services/filterEngine'
import { getResponseColor } from '@/utils/colors'

// Response order for impressions (No idea after Mixed)
const IMPRESSION_ORDER = ['Very positive', 'Mostly positive', 'Mixed', 'No idea', 'Mostly negative', 'Very negative']

// Legend labels (includes No idea with different gray)
const LEGEND_LABELS = ['Very positive', 'Mostly positive', 'Mixed', 'No idea', 'Mostly negative', 'Very negative']

// Sort responses according to impression order
const sortImpressionResponses = (responses: Array<{ label: string; percentage: number }>) => {
  return [...responses].sort((a, b) => {
    const aIdx = IMPRESSION_ORDER.indexOf(a.label)
    const bIdx = IMPRESSION_ORDER.indexOf(b.label)
    if (aIdx === -1) return 1
    if (bIdx === -1) return -1
    return aIdx - bIdx
  })
}

const PROVINCIAL_LEADERS = [
  { id: 'Impression_Holt', label: 'Susan Holt, Premier of New Brunswick' },
  { id: 'Impression_Kinew', label: 'Wab Kinew, Premier of Manitoba' },
  { id: 'Impression_Moe', label: 'Scott Moe, Premier of Saskatchewan' },
  { id: 'Impression_Smith', label: 'Danielle Smith, Premier of Alberta' },
  { id: 'Impression_Akeeagok', label: 'P.J. Akeeagok, Premier of Nunavut' },
  { id: 'Impression_Hogan', label: 'John Hogan, Premier of Newfoundland and Labrador' },
  { id: 'Impression_Houston', label: 'Tim Houston, Premier of Nova Scotia' },
  { id: 'Impression_Lantz', label: 'Rob Lantz, Premier of PEI' },
  { id: 'Impression_Ford', label: 'Doug Ford, Premier of Ontario' },
  { id: 'Impression_Simpson', label: 'R.J. Simpson, Premier of Northwest Territories' },
  { id: 'Impression_Eby', label: 'David Eby, Premier of British Columbia' },
  { id: 'Impression_Legault', label: 'François Legault, Premier of Quebec' },
  { id: 'Impression_Pemberton', label: 'Mike Pemberton, Premier of the Yukon' },
] as const

export function ProvincialLeaders() {
  const { state } = useApp()
  const { data, filters } = state
  const { selectedBreak, selectedProvince } = filters

  const breakDef = data?.breaks.find(b => b.id === selectedBreak)
  const { breakValues: filteredBreakValues } = useFilteredBreakValues()
  const breakValues = selectedBreak !== 'total' && breakDef ? filteredBreakValues : ['total']
  const isBreakActive = selectedBreak !== 'total'
  const breakName = breakDef?.name || 'Total'
  const displayMap = breakDef?.displayMap || {}
  const getDisplayName = (value: string) => displayMap[value] || value

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Provincial Leaders</h1>
        <span className="text-sm text-slate-500 dark:text-slate-400">Break: {breakName}</span>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
        <p className="text-slate-600 dark:text-slate-300 mb-6">
          What is your overall impression of the following provincial/territorial leaders?
        </p>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-6 pb-4 border-b border-slate-100 dark:border-slate-700">
          {LEGEND_LABELS.map(label => (
            <div key={label} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: getResponseColor('impression', label) }}
              />
              <span className="text-xs text-slate-600 dark:text-slate-400">{label}</span>
            </div>
          ))}
        </div>

        <div className="space-y-6">
          {!data ? (
            <p className="text-slate-500 dark:text-slate-400">Loading...</p>
          ) : (
            PROVINCIAL_LEADERS.map(({ id, label }) => {
              const question = data.questions[id]
              if (!question) return null

              const rows = breakValues.map(value => {
                const filterKey = buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined, selectedProvince)
                let questionData = question.data[filterKey]
                if (!questionData && selectedProvince) {
                  questionData = question.data[buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined)]
                }
                return { value, filterKey, data: questionData }
              }).filter(row => row.data)

              if (rows.length === 0) return null

              return (
                <div key={id} className="border-b border-slate-100 dark:border-slate-700 pb-4 last:border-0 last:pb-0">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">{label}</h3>
                  <div className="space-y-2">
                    {rows.map(row => (
                      <div key={row.filterKey} className="flex items-center gap-2">
                        <div className="w-32 text-xs text-slate-500 dark:text-slate-400 text-right shrink-0" title={isBreakActive ? getDisplayName(row.value) : 'National Sample'}>
                          {isBreakActive ? getDisplayName(row.value) : 'National Sample'}
                        </div>
                        <div className="flex-1">
                          <HorizontalStackedBar
                            segments={sortImpressionResponses(row.data!.responses)}
                            scaleType="impression"
                            height={isBreakActive ? 24 : 28}
                            showLabels={false}
                            normalize={true}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
